import React from 'react';
import { Link } from 'react-router-dom';
import Lottie from 'lottie-react';
import logoAnimation from '../assets/bbyb-logo-lottie.json';

const appLogo = new URL('../assets/bbyb-logo.png', import.meta.url);

const Feature = ({ icon, title, children }) => (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(137,81,41,0.1)' }}>
                {icon}
            </div>
            <h2 className="text-lg font-bold text-gray-800">{title}</h2>
        </div>
        <div className="text-sm text-gray-600 leading-relaxed space-y-2">{children}</div>
    </section>
);

const IntroPage = () => {
    return (
        <div className="min-h-screen" style={{ backgroundColor: '#f9f4f0' }}>
            {/* ── Header ── */}
            <header className="text-white shadow-lg" style={{ background: 'linear-gradient(to right, #660F09, #895129)' }}>
                <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition">
                        <img src={appLogo.href} alt="BrickByBrickVault" className="h-7 w-auto rounded" />
                        <span className="text-lg font-bold tracking-tight">BrickByBrickVault</span>
                    </Link>
                    <Link
                        to="/"
                        className="text-sm font-medium px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition"
                    >
                        Back to app
                    </Link>
                </div>
            </header>

            {/* ── Hero ── */}
            <div className="max-w-4xl mx-auto px-4 pt-10 sm:px-6 sm:pt-14 text-center">
                <Lottie animationData={logoAnimation} loop={false} style={{ width: 140, height: 140, margin: '0 auto' }} />
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mt-4 mb-3">
                    Your files. Your cloud. Your rules.
                </h1>
                <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
                    BrickByBrickVault is a private file management platform that puts you in control.
                    Upload, organize, and access your files from anywhere — backed by storage you own.
                </p>
            </div>

            {/* ── Features ── */}
            <main className="max-w-4xl mx-auto px-4 py-10 sm:px-6 sm:py-14 space-y-6">

                <Feature
                    icon={<svg className="w-5 h-5" style={{ color: '#895129' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
                    title="Private & Secure"
                >
                    <p>
                        Every file is protected behind JWT authentication. Your data stays on infrastructure you control —
                        no third-party scanning, no ads, no surprises. Access tokens are short-lived and refresh tokens
                        are hashed in the database.
                    </p>
                </Feature>

                <Feature
                    icon={<svg className="w-5 h-5" style={{ color: '#895129' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>}
                    title="Upload Anything"
                >
                    <p>
                        Drag and drop files or tap the upload button. BrickByBrickVault handles images, videos, documents —
                        anything you throw at it. Large files use presigned URLs for fast, direct-to-storage uploads.
                    </p>
                </Feature>

                <Feature
                    icon={<svg className="w-5 h-5" style={{ color: '#895129' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>}
                    title="Flexible Storage"
                >
                    <p>
                        Start with cloud storage (S3/Backblaze B2), then expand by connecting your own hardware as
                        a MinIO endpoint. Old files automatically move to cold storage to save costs — you choose the threshold.
                    </p>
                </Feature>

                <Feature
                    icon={<svg className="w-5 h-5" style={{ color: '#895129' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>}
                    title="Access Everywhere"
                >
                    <p>
                        Use the web app from any browser or the native mobile app on iOS and Android.
                        Your files sync instantly — upload on your phone, view on your desktop.
                    </p>
                </Feature>

                <Feature
                    icon={<svg className="w-5 h-5" style={{ color: '#895129' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                    title="You're in Control"
                >
                    <p>
                        Set your own storage quotas, manage cold storage thresholds, and configure hardware endpoints —
                        all from the Settings page. Admins can manage users, invites, and monitor the entire platform.
                    </p>
                </Feature>

                {/* Footer */}
                <div className="text-center pt-6 text-xs text-gray-400">
                    <p>BrickByBrickVault — built brick by brick.</p>
                </div>
            </main>
        </div>
    );
};

export default IntroPage;
